import { Rule } from "@iterate/common/types/shared";
import { formatDate } from "date-fns";
import * as React from "react";
import { Button, FloatingLabel, Form, Modal, Table } from "react-bootstrap";
import { CategorySelect } from "src/components/CategorySelect";
import { trpc } from "src/trpc";
import { formatCurrency } from "src/utils/currency";
import { useBackButton } from "src/hooks/useBackButton";
import styled from "styled-components";

export interface Props {
  rule: Omit<Rule, "categoryId"> & {
    categoryId: Rule["categoryId"] | null;
  };
  onClose(): void;
}

interface Form {
  match: string;
  label: string | null;
  categoryId: string | null;
}

export function RuleForm({ rule, onClose }: Props) {
  useBackButton(onClose);
  const utils = trpc.useUtils();
  const { mutateAsync: upsertRule } = trpc.upsertRule.useMutation();
  const [form, setForm] = React.useState<Form>({
    match: rule.match,
    label: rule.label,
    categoryId: rule.categoryId,
  });

  const { match, categoryId, label } = form;
  const { data: { results = [], count } = {} } = trpc.testRule.useQuery({
    match,
  });

  const changeMatch = (match: string) => setForm({ ...form, match });
  const changeLabel = (label: string) => setForm({ ...form, label });
  const setCategory = (categoryId: string | null) => setForm({ ...form, categoryId });

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (categoryId && results.length > 0) {
      await upsertRule({ rule: { id: rule.id, match, categoryId, label } });
      await utils.getTransactions.invalidate();
      await utils.getAggregateSpend.invalidate();
      await utils.getRules.invalidate();
      onClose();
    }
  };

  return (
    <Modal show onHide={onClose}>
      <Form onSubmit={onSave}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <CategorySelect value={categoryId} onChange={setCategory} />
          </Form.Group>
          <FloatingLabel label="Label" className="mb-3">
            <Form.Control value={form.label ?? ""} onChange={(e) => changeLabel(e.target.value)} />
          </FloatingLabel>
          <FloatingLabel label="Match" className="mb-3">
            <Form.Control value={form.match} onChange={(e) => changeMatch(e.target.value)} />
          </FloatingLabel>
          {count && (
            <Form.Group className="mb-3">
              <Form.Label>Results found: {count}</Form.Label>
            </Form.Group>
          )}
          {results.length > 0 && (
            <Table size="sm" striped bordered>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {results.map((t) => (
                  <tr key={t.id}>
                    <td>{formatDate(t.date, "yyyy-MM-dd")}</td>
                    <td>{t.description}</td>
                    <Amount $amount={t.amount}>{formatCurrency(Math.abs(t.amount))}</Amount>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="submit" disabled={!form.categoryId}>
            Save Rule
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

const Amount = styled.td<{ $amount: number }>`
  color: ${({ $amount }) => ($amount > 0 ? "green" : "red")} !important;
  font-size: 14px;
  font-family: monospace;
  text-align: right;
`;
